import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, Search, Plus, Trash2, Save, CheckCircle2, AlertTriangle, Lock,
  Mail, Linkedin, ExternalLink, ShieldCheck, Cpu, Pencil, X, RotateCcw,
} from "lucide-react";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import api from "@/lib/api";
import { studioApi, useStudio } from "./studioApi";
import { packageCheckLabels } from "../website-production/packageText";

const PHASES = [
  ["recherche", "Recherche"], ["fakten_rechte", "Fakten / Rechte"], ["kunde_ziel_verbesserungen", "Kunde / Ziel / Verbesserungen"],
  ["web_master", "Web-Master"], ["web_build_qa", "Web-Build / QA"], ["app_gate", "App-Gate"], ["app_build_qa", "App-Build / QA"],
  ["verkaufsmaterial", "Verkaufsmaterial"], ["finale_abnahme", "Finale Abnahme"],
];
const TABS = [["recherche", "Recherche"], ["profil", "Produktionsprofil"], ["paket", "Verkaufspaket"], ["nachrichten", "E-Mail / LinkedIn"]];
const MAX_HYPOTHESIS_ITEMS = 8;
const cls = "w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#A3E635]";
const Field = ({ t, children }) => {
  const id = children.props["data-testid"] || children.props.id;
  const control = id ? React.cloneElement(children, { id }) : children;
  return <div><label htmlFor={id} className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label><div className="mt-1">{control}</div></div>;
};

function PhaseStepper({ detail, onAdvance, advancing }) {
  const { t } = useStudio();
  const curIdx = PHASES.findIndex(([k]) => k === detail.studio_state.phase);
  const stale = detail.stale_phases || [];
  const staleKeys = new Set(stale.map((s) => s.phase));
  return (
    <div className="mb-5">
      <div className="flex gap-2 flex-wrap" data-testid="studio-phase-stepper">
        {PHASES.map(([key, label], i) => {
          const isStale = i < curIdx && staleKeys.has(key);
          const state = isStale ? "stale" : i < curIdx ? "done" : i === curIdx ? "current" : "open";
          return (
            <span key={key} data-testid={`studio-phase-${key}`} title={isStale ? stale.find((s) => s.phase === key)?.reason : undefined}
              className={`text-[11px] px-2.5 py-1.5 rounded-full border font-semibold inline-flex items-center gap-1 ${
                state === "stale" ? "bg-amber-400/15 text-amber-300 border-amber-400/30" :
                state === "done" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" :
                state === "current" ? "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30" : "bg-white/5 text-gray-500 border-white/10"}`}>
              {state === "stale" && <RotateCcw className="w-3 h-3" />} {t(label)}
            </span>
          );
        })}
        {curIdx < PHASES.length - 1 && (
          <button onClick={onAdvance} disabled={advancing} data-testid="studio-phase-advance"
            className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.08] disabled:opacity-40">
            {t("Nächste Phase freigeben")} →
          </button>
        )}
      </div>
      {stale.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="studio-stale-phases">
          {stale.map((s) => (
            <p key={s.phase} className="text-xs text-amber-200 flex items-center gap-1.5" data-testid={`studio-stale-${s.phase}`}>
              <RotateCcw className="w-3.5 h-3.5 shrink-0" /> {t("Veraltet")} «{t(s.phase_label)}»: {t(s.reason)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function ResearchTab({ orderId, detail, reload }) {
  const { t, fmtDate, err } = useStudio();
  const [scanning, setScanning] = useState(false);
  const [refUrl, setRefUrl] = useState("");
  const [addingRef, setAddingRef] = useState(false);
  const [items, setItems] = useState(detail.research.hypothesis || []);
  const [savingHyp, setSavingHyp] = useState(false);
  const scanningRef = useRef(false);
  const addingRefRef = useRef(false);
  const savingHypRef = useRef(false);
  const removingRef = useRef(new Set());
  const [removingIds, setRemovingIds] = useState(new Set());
  // Ungespeicherte Bearbeitung am Verbesserungsbrief darf nicht verloren gehen, nur weil ein
  // Referenz-/Scan-Reload die gemeinsame research.revision weiterzählt. dirtyRef merkt sich,
  // ob der Nutzer seit dem letzten Speichern/Laden selbst etwas verändert hat.
  const dirtyRef = useRef(false);
  const lastRevisionRef = useRef(detail.research.revision);
  useEffect(() => {
    if (detail.research.revision === lastRevisionRef.current) return;
    lastRevisionRef.current = detail.research.revision;
    if (dirtyRef.current) return; // eigene, noch ungespeicherte Texte nicht durch fremden Reload überschreiben
    setItems(detail.research.hypothesis || []);
  }, [detail.research.revision]);

  const scan = async () => {
    if (scanningRef.current) return;
    scanningRef.current = true; setScanning(true);
    try { await studioApi.scanSource(orderId); toast.success(t("Originalseite abgerufen")); await reload(); }
    catch (e) { toast.error(err(e, "Abruf fehlgeschlagen.")); }
    finally { scanningRef.current = false; setScanning(false); }
  };
  const addRef = async () => {
    if (addingRefRef.current || !refUrl.trim()) return;
    addingRefRef.current = true; setAddingRef(true);
    try { await studioApi.addReference(orderId, detail.research.revision, refUrl.trim()); setRefUrl(""); toast.success(t("Referenz ergänzt")); await reload(); }
    catch (e) { toast.error(err(e, "Referenz fehlgeschlagen.")); }
    finally { addingRefRef.current = false; setAddingRef(false); }
  };
  const removeRef = async (refId) => {
    if (removingRef.current.has(refId)) return;
    removingRef.current.add(refId); setRemovingIds(new Set(removingRef.current));
    try { await studioApi.removeReference(orderId, refId, detail.research.revision); await reload(); }
    catch (e) { toast.error(err(e, "Entfernen fehlgeschlagen.")); }
    finally { removingRef.current.delete(refId); setRemovingIds(new Set(removingRef.current)); }
  };
  const updateItem = (i, field, value) => { dirtyRef.current = true; setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [field]: value } : it))); };
  const addItem = () => { dirtyRef.current = true; setItems((arr) => (arr.length >= MAX_HYPOTHESIS_ITEMS ? arr : [...arr, { observation: "", idea: "", implementation: "", benefit: "" }])); };
  const removeItem = (i) => { dirtyRef.current = true; setItems((arr) => arr.filter((_, idx) => idx !== i)); };

  const saveHypothesis = async () => {
    if (savingHypRef.current) return;
    savingHypRef.current = true; setSavingHyp(true);
    try {
      await studioApi.saveHypothesis(orderId, detail.research.revision, items);
      dirtyRef.current = false; // erfolgreich gespeichert: künftige Reloads dürfen items wieder synchronisieren
      toast.success(t("Verbesserungsbrief gespeichert")); await reload();
    }
    catch (e) { toast.error(err(e, "Speichern fehlgeschlagen.")); } // dirtyRef bleibt gesetzt: Eingaben bleiben erhalten
    finally { savingHypRef.current = false; setSavingHyp(false); }
  };


  const source = detail.research.source;
  const refStatus = detail.references_status;

  return (
    <div className="space-y-5" data-testid="studio-tab-recherche">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-bold flex items-center gap-2"><Search className="w-4 h-4 text-[#A3E635]" /> {t("Originalseite (sicherer Abruf)")}</h3>
          <button onClick={scan} disabled={scanning} data-testid="studio-scan-source-btn" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-4 rounded-lg text-sm">{scanning ? t("Wird abgerufen…") : t("Jetzt abrufen")}</button>
        </div>
        {!source ? <p className="text-sm text-gray-500 mt-3">{t("Noch nicht abgerufen.")}</p> : (
          <div className="mt-3 text-sm" data-testid="studio-source-result">
            <p className="text-gray-400">{t("Abgerufen")}: {fmtDate(source.fetched_at)} {source.http_status != null && `· HTTP ${source.http_status}`}</p>
            {source.ok && source.facts ? (
              <ul className="mt-2 grid sm:grid-cols-2 gap-1.5 text-xs text-gray-300">
                <li>{source.facts.is_https ? "✓" : "✗"} {t("HTTPS")}</li>
                <li>{source.facts.has_viewport_meta ? "✓" : "✗"} {t("Mobil-Viewport")}</li>
                <li>{source.facts.meta_description ? "✓" : "✗"} {t("Meta-Beschreibung")}</li>
                <li>{source.facts.has_contact_link ? "✓" : "✗"} {t("Kontaktlink erkannt")}</li>
              </ul>
            ) : <p className="text-red-300 mt-1" role="alert">{source.error}</p>}
            {source.note && <p className="text-[11px] text-gray-500 mt-2">{t(source.note)}</p>}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="font-bold flex items-center gap-2"><Globe className="w-4 h-4 text-[#A3E635]" /> {t("Branchenreferenzen")}</h3>
        <p className={`text-sm mt-2 ${refStatus.status === "manuell_ausreichend" ? "text-emerald-300" : "text-amber-200"}`} data-testid="studio-references-status">
          {refStatus.evidenced}/{refStatus.target_min}+ ({t("Ziel")} {refStatus.target}) — {t(refStatus.note)}
        </p>
        <div className="flex gap-2 mt-3">
          <input value={refUrl} onChange={(e) => setRefUrl(e.target.value)} disabled={addingRef} placeholder="www.referenzfirma.ch" data-testid="studio-reference-url-input" className={cls} />
          <button onClick={addRef} disabled={addingRef || !refUrl.trim()} data-testid="studio-reference-add-btn" className="shrink-0 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 text-sm disabled:opacity-40"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 space-y-2">
          {(detail.research.references || []).map((r) => (
            <div key={r.ref_id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2 text-sm min-w-0" data-testid={`studio-reference-${r.ref_id}`}>
              <span className="truncate">{r.url} {r.ok && r.facts ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline ml-1" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 inline ml-1" />}</span>
              <button onClick={() => removeRef(r.ref_id)} disabled={removingIds.has(r.ref_id)} data-testid={`studio-reference-remove-${r.ref_id}`} className="shrink-0 text-gray-500 hover:text-red-300 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold">{t("Verbesserungsbrief (regelbasiert, editierbar)")}</h3>
          <button onClick={saveHypothesis} disabled={savingHyp} data-testid="studio-hypothesis-save" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-4 rounded-lg text-sm inline-flex items-center gap-2"><Save className="w-4 h-4" /> {t("Speichern")}</button>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">{t("Kein KI-Text – Vorschläge aus echten Befunden der Originalseite, keine garantierten Erfolge.")}</p>
        <fieldset disabled={savingHyp} className="space-y-3 mt-3">
          {items.map((it, i) => (
            <div key={i} className="bg-white/5 rounded-lg p-3 space-y-2" data-testid={`studio-hypothesis-item-${i}`}>
              <Field t={t("Beobachtung")}><textarea value={it.observation} onChange={(e) => updateItem(i, "observation", e.target.value)} rows={2} maxLength={500} data-testid={`studio-hyp-observation-${i}`} className={cls} /></Field>
              <Field t={t("Idee")}><textarea value={it.idea} onChange={(e) => updateItem(i, "idea", e.target.value)} rows={2} maxLength={500} data-testid={`studio-hyp-idea-${i}`} className={cls} /></Field>
              <Field t={t("Umsetzung")}><textarea value={it.implementation} onChange={(e) => updateItem(i, "implementation", e.target.value)} rows={2} maxLength={500} data-testid={`studio-hyp-implementation-${i}`} className={cls} /></Field>
              <Field t={t("Prüfbarer Vorteil")}><textarea value={it.benefit} onChange={(e) => updateItem(i, "benefit", e.target.value)} rows={2} maxLength={500} data-testid={`studio-hyp-benefit-${i}`} className={cls} /></Field>
              <button onClick={() => removeItem(i)} data-testid={`studio-hyp-remove-${i}`} className="text-xs text-gray-500 hover:text-red-300 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> {t("Entfernen")}</button>
            </div>
          ))}
          <button onClick={addItem} disabled={items.length >= MAX_HYPOTHESIS_ITEMS} data-testid="studio-hyp-add" className="text-sm text-[#A3E635] hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1"><Plus className="w-4 h-4" /> {t("Punkt hinzufügen")}</button>
          {items.length >= MAX_HYPOTHESIS_ITEMS && <p className="text-[11px] text-gray-500" data-testid="studio-hyp-limit-note">{t("Höchstens 8 Punkte möglich.")}</p>}
        </fieldset>
      </Card>
    </div>
  );
}

function ProfileTab({ orderId, detail, reload }) {
  const { t, err } = useStudio();
  const [runners, setRunners] = useState([]);
  const [form, setForm] = useState({ device_runner_id: detail.profile.device_runner_id || "", max_jobs_per_day: detail.profile.max_jobs_per_day ?? "", ai_model_preference: detail.profile.ai_model_preference || "" });
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  useEffect(() => { api.get("/admin/pc-relay/status").then((r) => setRunners(r.data.runners || [])).catch(() => {}); }, []);

  const limitInvalid = form.max_jobs_per_day !== "" && (Number(form.max_jobs_per_day) < 1 || Number(form.max_jobs_per_day) > 500);

  const save = async () => {
    if (savingRef.current || limitInvalid) return;
    savingRef.current = true; setSaving(true);
    try {
      await studioApi.saveProfile(orderId, { revision: detail.profile.revision, device_runner_id: form.device_runner_id || null, max_jobs_per_day: form.max_jobs_per_day === "" ? null : Number(form.max_jobs_per_day), ai_model_preference: form.ai_model_preference || null });
      toast.success(t("Produktionsprofil gespeichert")); await reload();
    } catch (e) { toast.error(err(e, "Speichern fehlgeschlagen.")); }
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <Card className="p-5 max-w-xl space-y-4" data-testid="studio-tab-profil">
      <h3 className="font-bold flex items-center gap-2"><Cpu className="w-4 h-4 text-[#A3E635]" /> {t("Produktionsprofil")}</h3>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div><div className="text-[11px] uppercase tracking-wider text-gray-500">{t("Sprache")}</div><div className="mt-1">{(detail.order.languages || []).join(", ") || "de"}</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-gray-500">{t("Vorschauumfang")}</div><div className="mt-1">{detail.order.page_scope} {t("Seiten")}</div></div>
      </div>
      <fieldset disabled={saving} className="space-y-4">
      <Field t={t("Zugewiesenes Gerät")}>
        <select value={form.device_runner_id} onChange={(e) => setForm({ ...form, device_runner_id: e.target.value })} data-testid="studio-profile-device" className={cls}>
          <option value="">{t("— kein Gerät —")}</option>
          {runners.map((r) => <option key={r.runner_id} value={r.runner_id} disabled={r.status !== "active"}>{r.label} ({r.status})</option>)}
        </select>
      </Field>
      <Field t={t("Auftragslimit pro Tag")}><input type="number" min={1} max={500} value={form.max_jobs_per_day} onChange={(e) => setForm({ ...form, max_jobs_per_day: e.target.value })} data-testid="studio-profile-limit" className={cls} /></Field>
      {limitInvalid && <p className="text-xs text-red-300" data-testid="studio-profile-limit-error">{t("Bitte einen Wert zwischen 1 und 500 eingeben.")}</p>}
      <p className="text-[11px] text-amber-200" data-testid="studio-profile-limit-note">{t(detail.profile.limit_note)}</p>
      <Field t={t("KI-Modell-Vorgabe (freie Notiz, kein geprüfter Anbieterkatalog, noch nicht aktiv)")}>
        <select value={form.ai_model_preference} onChange={(e) => setForm({ ...form, ai_model_preference: e.target.value })} data-testid="studio-profile-ai-model" className={cls}>
          <option value="">{t("— keine Vorgabe —")}</option>
          <option value="claude-sonnet">{t("Claude (Vorgabe, nicht geprüft)")}</option>
          <option value="gpt">{t("GPT (Vorgabe, nicht geprüft)")}</option>
        </select>
      </Field>
      </fieldset>
      <p className="text-[11px] text-gray-500 flex items-center gap-1.5" data-testid="studio-ai-blocked-note"><Lock className="w-3.5 h-3.5" /> {detail.ai_provider_connected ? t("Anbieter verbunden") : t("Kein bestätigter Anbieteranschluss – KI-Text bleibt blockiert.")}</p>
      <button onClick={save} disabled={saving || limitInvalid} data-testid="studio-profile-save" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-5 rounded-lg text-sm">{t("Speichern")}</button>
    </Card>
  );
}

function PackageTab({ detail }) {
  const { t } = useStudio();
  const checks = detail.package_checks || {};
  return (
    <Card className="p-5 max-w-xl space-y-3" data-testid="studio-tab-paket">
      <h3 className="font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#A3E635]" /> {t("Verkaufspaket")}</h3>
      <p className="text-sm text-gray-400">{t("Fakten/Rechte, Dokumente und Vorschauen werden im bestehenden Website-Produktions-Paket gepflegt (keine zweite Datenhaltung).")}</p>
      <ul className="text-sm space-y-1.5">
        {Object.entries(checks).map(([k, v]) => (
          <li key={k} className="flex items-center justify-between gap-3" data-testid={`studio-package-check-${k}`}>
            <span className="flex items-center gap-2">{v ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />} {t(packageCheckLabels[k] || k)}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold shrink-0 ${v ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-amber-400/15 text-amber-300 border-amber-400/30"}`}>{v ? t("Erfüllt") : t("Offen")}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-4">
        <Link to={`/admin/website-production/${detail.order.id}/package`} data-testid="studio-package-link" className="inline-flex items-center gap-2 text-sm text-[#A3E635] hover:underline">{t("Verkaufspaket öffnen")} <ExternalLink className="w-3.5 h-3.5" /></Link>
        <Link to={`/admin/website-production/${detail.order.id}/edit`} data-testid="studio-edit-order-link" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white hover:underline"><Pencil className="w-3.5 h-3.5" /> {t("Auftragsdaten bearbeiten")}</Link>
      </div>
    </Card>
  );
}

function MessagesTab({ orderId }) {
  const { t, fmtDate, err } = useStudio();
  const [messages, setMessages] = useState(null);
  const [messagesError, setMessagesError] = useState(null);
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const requestRef = useRef(0);
  const creatingRef = useRef(false);
  const savingRef = useRef(false);
  const busyRef = useRef(new Set());

  const load = () => {
    const reqId = ++requestRef.current;
    return studioApi.listMessages(orderId).then((r) => {
      if (requestRef.current !== reqId) return; // überholt: nur die letzte Anfrage gewinnt
      setMessages(r.data); setMessagesError(null);
    }).catch((e) => {
      if (requestRef.current !== reqId) return;
      // bestehende, bereits geladene Entwürfe bei einem Nachlade-Fehler nicht verwerfen –
      // nur eine Fehlermeldung mit Wiederholung zusätzlich anzeigen.
      setMessagesError(e);
    });
  };
  useEffect(() => { requestRef.current += 1; setMessages(null); setMessagesError(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orderId]);

  const withBusy = async (id, fn) => {
    if (busyRef.current.has(id)) return;
    busyRef.current.add(id); setBusyIds(new Set(busyRef.current));
    try { await fn(); }
    finally { busyRef.current.delete(id); setBusyIds(new Set(busyRef.current)); }
  };

  const create = async () => {
    if (creatingRef.current || !body.trim()) return;
    creatingRef.current = true; setCreating(true);
    try { await studioApi.createMessage(orderId, { channel, subject: channel === "email" ? subject : null, body }); setSubject(""); setBody(""); toast.success(t("Entwurf erstellt")); await load(); }
    catch (e) { toast.error(err(e, "Fehler.")); } // Eingaben bleiben bei Fehler erhalten (kein Reset von subject/body)
    finally { creatingRef.current = false; setCreating(false); }
  };
  const approve = (m) => withBusy(m.id, async () => {
    try { await studioApi.updateMessage(orderId, m.id, { revision: m.revision, status: "freigegeben" }); await load(); }
    catch (e) { toast.error(err(e, "Fehler.")); }
  });
  const remove = (m) => withBusy(m.id, async () => {
    try { await studioApi.deleteMessage(orderId, m.id, m.revision); await load(); }
    catch (e) { toast.error(err(e, "Fehler.")); }
  });
  const startEdit = (m) => { setEditingId(m.id); setEditSubject(m.subject || ""); setEditBody(m.body); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (m) => {
    if (savingRef.current || !editBody.trim()) return;
    savingRef.current = true; setSaving(true);
    // bewusst OHNE status: eine blosse Textänderung muss eine bestehende Freigabe
    // zurücksetzen (serverseitig), statt sie unverändert erneut zu bestätigen.
    try {
      await studioApi.updateMessage(orderId, m.id, { revision: m.revision, subject: m.channel === "email" ? editSubject : null, body: editBody });
      setEditingId(null); toast.success(t("Entwurf aktualisiert")); await load();
    } catch (e) { toast.error(err(e, "Fehler.")); } // Entwurf bleibt im Bearbeiten-Modus mit den eingegebenen Texten erhalten
    finally { savingRef.current = false; setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="studio-tab-nachrichten">
      <Card className="p-5 max-w-xl space-y-3">
        <h3 className="font-bold">{t("Neuer Entwurf")}</h3>
        <div className="flex gap-2">
          <button onClick={() => setChannel("email")} disabled={creating} data-testid="studio-msg-channel-email" className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 ${channel === "email" ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400"}`}><Mail className="w-3.5 h-3.5" /> {t("E-Mail")}</button>
          <button onClick={() => setChannel("linkedin")} disabled={creating} data-testid="studio-msg-channel-linkedin" className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 ${channel === "linkedin" ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400"}`}><Linkedin className="w-3.5 h-3.5" /> LinkedIn</button>
        </div>
        {channel === "email" && <Field t={t("Betreff")}><input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={creating} maxLength={200} data-testid="studio-msg-subject" className={cls} /></Field>}
        <Field t={t("Text")}><textarea value={body} onChange={(e) => setBody(e.target.value)} disabled={creating} rows={4} maxLength={5000} data-testid="studio-msg-body" className={cls} /></Field>
        <button onClick={create} disabled={creating || !body.trim()} data-testid="studio-msg-create" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-4 rounded-lg text-sm">{t("Entwurf speichern")}</button>
      </Card>
      {messagesError && (
        <Card className="p-4 border-red-400/25" data-testid="studio-msg-load-error">
          <p role="alert" className="text-sm text-red-200">{err(messagesError, "Entwürfe konnten nicht geladen werden.")}</p>
          <button onClick={load} data-testid="studio-msg-load-retry" className="mt-2 inline-flex items-center gap-2 text-sm text-[#A3E635] hover:underline"><RotateCcw className="w-4 h-4" /> {t("Erneut versuchen")}</button>
        </Card>
      )}
      {messages === null && !messagesError ? <Spinner /> : messages && messages.length === 0 ? <Empty text={t("Noch keine Entwürfe.")} /> : messages && (
        <div className="space-y-3">
          {messages.map((m) => {
            const busy = busyIds.has(m.id);
            return (
            <Card key={m.id} className="p-4" data-testid={`studio-msg-${m.id}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-gray-400 inline-flex items-center gap-1.5">{m.channel === "email" ? <Mail className="w-3.5 h-3.5" /> : <Linkedin className="w-3.5 h-3.5" />} {m.channel === "email" ? t("E-Mail") : "LinkedIn"} · {fmtDate(m.created_at)}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${m.status === "freigegeben" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-white/10 text-gray-300 border-white/10"}`} data-testid={`studio-msg-status-${m.id}`}>{m.status === "freigegeben" ? t("Freigegeben") : t("Entwurf")}</span>
              </div>
              {editingId === m.id ? (
                <div className="space-y-2 mt-3" data-testid={`studio-msg-edit-form-${m.id}`}>
                  {m.channel === "email" && <Field t={t("Betreff")}><input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} disabled={saving} maxLength={200} data-testid={`studio-msg-edit-subject-${m.id}`} className={cls} /></Field>}
                  <Field t={t("Text")}><textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} disabled={saving} rows={4} maxLength={5000} data-testid={`studio-msg-edit-body-${m.id}`} className={cls} /></Field>
                  {m.status === "freigegeben" && <p className="text-[11px] text-amber-200">{t("Eine Textänderung setzt die Freigabe zurück.")}</p>}
                  <div className="flex items-center gap-3">
                    <button onClick={() => saveEdit(m)} disabled={saving || !editBody.trim()} data-testid={`studio-msg-edit-save-${m.id}`} className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-1.5 px-4 rounded-lg text-xs inline-flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> {t("Speichern")}</button>
                    <button onClick={cancelEdit} disabled={saving} data-testid={`studio-msg-edit-cancel-${m.id}`} className="text-xs text-gray-500 hover:text-white inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> {t("Abbrechen")}</button>
                  </div>
                </div>
              ) : (
                <>
                  {m.subject && <p className="font-semibold mt-2 text-sm">{m.subject}</p>}
                  <p className="text-sm text-gray-400 mt-1 whitespace-pre-wrap">{m.body}</p>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    {!m.sent && <button onClick={() => startEdit(m)} disabled={busy} data-testid={`studio-msg-edit-${m.id}`} className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1 disabled:opacity-40"><Pencil className="w-3.5 h-3.5" /> {t("Bearbeiten")}</button>}
                    {m.status !== "freigegeben" && !m.sent && <button onClick={() => approve(m)} disabled={busy} data-testid={`studio-msg-approve-${m.id}`} className="text-xs text-[#A3E635] font-semibold hover:underline disabled:opacity-40">{t("Freigeben")}</button>}
                    {!m.sent && <button onClick={() => remove(m)} disabled={busy} data-testid={`studio-msg-delete-${m.id}`} className="text-xs text-gray-500 hover:text-red-300 disabled:opacity-40">{t("Löschen")}</button>}
                    <span className="text-xs text-gray-600 inline-flex items-center gap-1 ml-auto" title={t("Kein Anbieter verbunden – kein echter Versand/Veröffentlichung möglich.")}><Lock className="w-3 h-3" /> {t("Senden gesperrt")}</span>
                  </div>
                </>
              )}
            </Card>
          );})}
        </div>
      )}
    </div>
  );
}

export default function StudioWorkspace() {
  const { id } = useParams();
  const { t, err } = useStudio();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("recherche");
  const [advancing, setAdvancing] = useState(false);
  const requestRef = useRef(0);
  const advancingRef = useRef(false);
  // Immer frischer Auftrags-Scope, synchron bei jedem Render gesetzt (nicht erst im Effect).
  // Eine ältere reload()-Closure, die ein Kind-Tab (z.B. ResearchTab/ProfileTab) nach einem
  // bereits abgeschlossenen Mutationsaufruf noch aufruft, nachdem längst zu einem anderen
  // Auftrag gewechselt wurde, erkennt daran, dass sie nicht mehr aktuell ist, und setzt dann
  // gar nicht erst eine neue order()-Anfrage ab (kein Scope-Leck trotz gemeinsamem requestRef).
  const idRef = useRef(id);
  idRef.current = id;

  const reload = () => {
    const targetId = id; // Auftrags-Scope, den DIESE reload-Instanz beim Erzeugen hatte
    if (idRef.current !== targetId) return Promise.resolve(); // Auftrag bereits gewechselt: überholte Closure, keine Anfrage mehr absetzen
    const reqId = ++requestRef.current;
    return studioApi.order(targetId).then((r) => {
      if (idRef.current !== targetId || requestRef.current !== reqId) return; // überholt: nur die letzte Anfrage im aktuellen Auftrags-Scope gewinnt
      setDetail(r.data); setError(null);
    }).catch((e) => {
      if (idRef.current !== targetId || requestRef.current !== reqId) return;
      setError(e);
    });
  };
  useEffect(() => {
    // Auftragswechsel: alte Details/Fehler sofort verbergen, damit Formulare nie mit
    // der neuen Auftrags-ID aber dem Inhalt des vorherigen Auftrags bedient werden
    // können. Eine verspätete Antwort für den vorherigen Auftrag darf den neuen Scope
    // wegen der obigen idRef-/requestRef-Prüfung ohnehin nie mehr überschreiben.
    requestRef.current += 1;
    setDetail(null);
    setError(null);
    setTab("recherche");
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const advancePhase = async () => {
    if (advancingRef.current) return;
    const curIdx = PHASES.findIndex(([k]) => k === detail.studio_state.phase);
    const next = PHASES[curIdx + 1]?.[0];
    if (!next) return;
    advancingRef.current = true; setAdvancing(true);
    try { await studioApi.setPhase(id, detail.studio_state.revision, next); toast.success(t("Phase freigegeben")); await reload(); }
    catch (e) { toast.error(err(e, "Phase konnte nicht freigegeben werden.")); }
    finally { advancingRef.current = false; setAdvancing(false); }
  };

  if (error) return (
    <Card className="p-8 max-w-xl" data-testid="studio-workspace-error">
      <p role="alert" className="text-sm text-red-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {err(error, "Vorgang konnte nicht geladen werden.")}</p>
      <button onClick={reload} data-testid="studio-workspace-retry" className="mt-4 inline-flex items-center gap-2 text-sm text-[#A3E635] hover:underline"><RotateCcw className="w-4 h-4" /> {t("Erneut versuchen")}</button>
    </Card>
  );
  if (!detail) return <Spinner />;

  return (
    <div key={id} className="fade-in" data-testid="studio-workspace-page">
      <Link to="/admin/studio" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-3" data-testid="studio-back-link"><ArrowLeft className="w-4 h-4" /> {t("Alle Vorgänge")}</Link>
      <PageHeader title={detail.order.company} subtitle={detail.order.website_url}>
        <Link to={`/admin/website-production/${detail.order.id}/edit`} data-testid="studio-header-edit-order-link"
          className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2">
          <Pencil className="w-3.5 h-3.5" /> {t("Auftragsdaten bearbeiten")}
        </Link>
      </PageHeader>
      <PhaseStepper detail={detail} onAdvance={advancePhase} advancing={advancing} />
      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`studio-tab-btn-${key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab === key ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>
            {t(label)}
          </button>
        ))}
      </div>
      {tab === "recherche" && <ResearchTab orderId={id} detail={detail} reload={reload} />}
      {tab === "profil" && <ProfileTab orderId={id} detail={detail} reload={reload} />}
      {tab === "paket" && <PackageTab detail={detail} />}
      {tab === "nachrichten" && <MessagesTab orderId={id} />}
    </div>
  );
}
